import * as THREE from 'three';
import { EventBus } from '../core/EventBus';
import { WeaponUpgradeManager } from '../systems/WeaponUpgradeManager';

export interface WeaponConfig {
  name: string;
  damage: number;
  fireRate: number; // Rounds per second
  projectileSpeed: number;
  ammoMax: number;
  reloadTime: number; // Seconds
  spread: number; // Accuracy cone in radians
  automatic: boolean; // Can hold to fire
  projectileColor: THREE.Color;
  projectileSize: number;
}

export abstract class Weapon {
  protected config: WeaponConfig;
  protected currentAmmo: number;
  protected lastFireTime: number = 0;
  protected isReloading: boolean = false;
  protected reloadStartTime: number = 0;
  protected upgradeManager: WeaponUpgradeManager | null = null;

  constructor(config: WeaponConfig) {
    this.config = config;
    this.currentAmmo = config.ammoMax;
  }

  public setUpgradeManager(manager: WeaponUpgradeManager): void {
    this.upgradeManager = manager;
  }

  public canFire(): boolean {
    if (this.isReloading) return false;
    if (this.currentAmmo <= 0) return false;

    const now = performance.now();
    const effectiveFireRate = this.getEffectiveFireRate();
    const fireInterval = 1000 / effectiveFireRate;

    return now - this.lastFireTime >= fireInterval;
  }

  // Get fire rate with upgrade multipliers applied
  protected getEffectiveFireRate(): number {
    let rate = this.config.fireRate;
    if (this.upgradeManager) {
      rate *= this.upgradeManager.getFireRateMultiplier();
    }
    return rate;
  }

  // Get damage with upgrade multipliers applied
  protected getEffectiveDamage(): number {
    let damage = this.config.damage;
    if (this.upgradeManager) {
      damage *= this.upgradeManager.getDamageMultiplier();
    }
    return damage;
  }

  // Get projectile speed with upgrade multipliers applied
  protected getEffectiveProjectileSpeed(): number {
    let speed = this.config.projectileSpeed;
    if (this.upgradeManager) {
      speed *= this.upgradeManager.getProjectileSpeedMultiplier();
    }
    return speed;
  }

  // Get max ammo with upgrade multipliers applied
  protected getEffectiveMaxAmmo(): number {
    let maxAmmo = this.config.ammoMax;
    if (this.upgradeManager) {
      maxAmmo = Math.floor(maxAmmo * this.upgradeManager.getMagazineSizeMultiplier());
    }
    return maxAmmo;
  }

  public fire(origin: THREE.Vector3, direction: THREE.Vector3): boolean {
    if (!this.canFire()) return false;

    // Safely decrement ammo with bounds check
    this.currentAmmo = Math.max(0, this.currentAmmo - 1);
    this.lastFireTime = performance.now();

    // Apply spread
    const spreadDirection = this.applySpread(direction);

    // Create projectile (implemented by subclass)
    this.createProjectile(origin, spreadDirection);

    // Emit event with muzzle position and direction for effects
    EventBus.emit('weapon:fired', {
      weaponType: this.config.name,
      ammoLeft: this.currentAmmo,
      muzzlePosition: { x: origin.x, y: origin.y, z: origin.z },
      direction: { x: spreadDirection.x, y: spreadDirection.y, z: spreadDirection.z },
    });

    // Auto-reload if empty
    if (this.currentAmmo <= 0) {
      this.reload();
    }

    return true;
  }

  protected abstract createProjectile(
    origin: THREE.Vector3,
    direction: THREE.Vector3
  ): void;

  protected applySpread(direction: THREE.Vector3): THREE.Vector3 {
    if (this.config.spread === 0) return direction.clone().normalize();

    const spreadX = (Math.random() - 0.5) * this.config.spread;
    const spreadY = (Math.random() - 0.5) * this.config.spread;

    const right = new THREE.Vector3(1, 0, 0);
    const up = new THREE.Vector3(0, 1, 0);

    const spreadDir = direction.clone().normalize();
    spreadDir.applyAxisAngle(right, spreadX);
    spreadDir.applyAxisAngle(up, spreadY);

    return spreadDir;
  }

  public reload(): void {
    if (this.isReloading) return;
    if (this.currentAmmo === this.config.ammoMax) return;

    this.isReloading = true;
    this.reloadStartTime = performance.now();

    EventBus.emit('weapon:reloading', {
      weaponType: this.config.name,
      reloadTime: this.config.reloadTime,
    });
  }

  public update(): void {
    if (this.isReloading) {
      const elapsed = (performance.now() - this.reloadStartTime) / 1000;
      if (elapsed >= this.config.reloadTime) {
        this.isReloading = false;
        this.currentAmmo = this.getEffectiveMaxAmmo();

        EventBus.emit('weapon:reloaded', {
          weaponType: this.config.name,
        });
      }
    }
  }

  public getAmmo(): number {
    return this.currentAmmo;
  }

  public getMaxAmmo(): number {
    return this.getEffectiveMaxAmmo();
  }

  public refillAmmo(amount: number): void {
    const maxAmmo = this.getEffectiveMaxAmmo();
    this.currentAmmo = Math.min(maxAmmo, this.currentAmmo + amount);
  }

  public getName(): string {
    return this.config.name;
  }

  public getConfig(): WeaponConfig {
    return this.config;
  }

  public isAutomatic(): boolean {
    return this.config.automatic;
  }

  public getReloadProgress(): number {
    if (!this.isReloading) return 1;
    const elapsed = (performance.now() - this.reloadStartTime) / 1000;
    return Math.min(elapsed / this.config.reloadTime, 1);
  }

  public isCurrentlyReloading(): boolean {
    return this.isReloading;
  }
}
